import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import WellnessMetrics from './components/WellnessMetrics';
import BookingManagement from './components/BookingManagement';
import WellnessAnalytics from './components/WellnessAnalytics';
import EESSchedulingCalendar from './components/EESSchedulingCalendar';
import GuestManagement from './components/GuestManagement';
import QuickSessionCreation from './components/QuickSessionCreation';

const WellnessHostDashboard = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [showQuickSessionForm, setShowQuickSessionForm] = useState(false);
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [activeView, setActiveView] = useState('overview');

  useEffect(() => {
    // Simulate initial data loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1200);

    // Simulate real-time wellness notifications
    const notificationTimer = setInterval(() => {
      const mockNotifications = [
        'New EES session booking from Sarah Johnson',
        'Guest wellness score improved: Michael Rodriguez (+15 points)',
        'Weekly package inquiry from new potential guest',
        'Session completed: Emma Thompson rated 5 stars'
      ];
      const randomNotification = mockNotifications[Math.floor(Math.random() * mockNotifications.length)];
      setNotifications(prev => [{
        id: Date.now(),
        message: randomNotification,
        timestamp: new Date(),
        type: 'booking'
      }, ...prev.slice(0, 4)]);
    }, 35000);

    return () => {
      clearTimeout(timer);
      clearInterval(notificationTimer);
    };
  }, []);

  const handleBulkAction = (action, bookingIds) => {
    console.log(`Bulk action ${action} for bookings:`, bookingIds);
  };

  const handleQuickSession = () => {
    setShowQuickSessionForm(true);
  };

  const handleSessionSubmit = (sessionData) => {
    console.log('New session data:', sessionData);
    setShowQuickSessionForm(false);
    // Implement session creation logic
  };

  const navItems = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'calendar', label: 'Calendar', icon: 'Calendar' },
    { id: 'guests', label: 'Guests', icon: 'Users' },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3' }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 lg:pt-18">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Dashboard Skeleton */}
            <div className="mb-8">
              <div className="h-8 bg-secondary-100 rounded w-1/3 mb-4 skeleton"></div>
              <div className="h-4 bg-secondary-100 rounded w-1/2 skeleton"></div>
            </div>
            
            {/* Metrics Skeleton */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-surface p-6 rounded-lg shadow-elevation-1">
                  <div className="h-4 bg-secondary-100 rounded w-1/2 mb-2 skeleton"></div>
                  <div className="h-8 bg-secondary-100 rounded w-3/4 skeleton"></div>
                </div>
              ))}
            </div>
            
            {/* Content Skeleton */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <div className="h-64 bg-secondary-100 rounded-lg skeleton mb-8"></div>
                <div className="h-96 bg-secondary-100 rounded-lg skeleton"></div>
              </div>
              <div className="space-y-8">
                <div className="h-64 bg-secondary-100 rounded-lg skeleton"></div>
                <div className="h-64 bg-secondary-100 rounded-lg skeleton"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16 lg:pt-18">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Dashboard Header */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-text-primary font-heading mb-2">
                  Wellness Host Dashboard
                </h1>
                <p className="text-text-secondary">
                  Manage EES sessions, track guest wellness journeys, and optimize your wellness center
                </p>
              </div>
              <div className="mt-4 md:mt-0 flex items-center space-x-3">
                <button
                  onClick={handleQuickSession}
                  className="bg-primary text-white px-6 py-3 rounded-md font-medium hover:bg-primary-700 transition-all duration-200 ease-out micro-interaction shadow-elevation-1"
                >
                  Create New Session
                </button>
                <button className="bg-accent text-white px-6 py-3 rounded-md font-medium hover:bg-accent-600 transition-all duration-200 ease-out micro-interaction shadow-elevation-1">
                  Wellness Report
                </button>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="mb-8">
            <div className="border-b border-border">
              <nav className="-mb-px flex space-x-8 overflow-x-auto">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveView(item.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-all duration-200 ${
                      activeView === item.id
                        ? 'border-primary text-primary' :'border-transparent text-text-secondary hover:text-text-primary hover:border-secondary-300'
                    }`}
                  >
                    <span>{item.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Performance Metrics - Always visible */}
          <WellnessMetrics />

          {/* Dynamic Content Based on Active View */}
          {activeView === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
              {/* Main Content Area */}
              <div className="lg:col-span-2 space-y-8">
                {/* Booking Management */}
                <BookingManagement 
                  selectedBookings={selectedBookings}
                  onSelectionChange={setSelectedBookings}
                  onBulkAction={handleBulkAction}
                />
                
                {/* Analytics Section */}
                <WellnessAnalytics />
              </div>

              {/* Sidebar */}
              <div className="space-y-8">
                {/* Recent Notifications */}
                <div className="bg-surface rounded-lg shadow-elevation-1 p-6">
                  <h3 className="text-lg font-semibold text-text-primary mb-4">Recent Activity</h3>
                  <div className="space-y-3">
                    {notifications.length > 0 ? (
                      notifications.slice(0, 5).map((notification) => (
                        <div key={notification.id} className="flex items-start space-x-3 p-3 bg-secondary-50 rounded-lg">
                          <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-text-primary">{notification.message}</p>
                            <p className="text-xs text-text-secondary mt-1">
                              {notification.timestamp.toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-text-secondary text-sm">No recent activity</p>
                    )}
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-surface rounded-lg shadow-elevation-1 p-6">
                  <h3 className="text-lg font-semibold text-text-primary mb-4">Quick Actions</h3>
                  <div className="space-y-3">
                    <button
                      onClick={handleQuickSession}
                      className="w-full flex items-center space-x-3 p-3 text-left bg-primary-50 text-primary rounded-lg hover:bg-primary-100 transition-all duration-200"
                    >
                      <div className="w-8 h-8 bg-primary rounded-md flex items-center justify-center">
                        <span className="text-white text-sm">+</span>
                      </div>
                      <span className="font-medium">New EES Session</span>
                    </button>
                    
                    <button className="w-full flex items-center space-x-3 p-3 text-left bg-accent-50 text-accent-600 rounded-lg hover:bg-accent-100 transition-all duration-200">
                      <div className="w-8 h-8 bg-accent-600 rounded-md flex items-center justify-center">
                        <span className="text-white text-sm">📊</span>
                      </div>
                      <span className="font-medium">Generate Report</span>
                    </button>
                    
                    <button className="w-full flex items-center space-x-3 p-3 text-left bg-success-50 text-success-600 rounded-lg hover:bg-success-100 transition-all duration-200">
                      <div className="w-8 h-8 bg-success-600 rounded-md flex items-center justify-center">
                        <span className="text-white text-sm">💬</span>
                      </div>
                      <span className="font-medium">Message Guests</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeView === 'calendar' && (
            <div className="mb-8">
              <EESSchedulingCalendar />
            </div>
          )}

          {activeView === 'guests' && (
            <div className="mb-8">
              <GuestManagement />
            </div>
          )}

          {activeView === 'analytics' && (
            <div className="mb-8">
              <WellnessAnalytics />
            </div>
          )}
        </div>
      </main>

      {/* Quick Session Creation Modal */}
      {showQuickSessionForm && (
        <QuickSessionCreation 
          onClose={() => setShowQuickSessionForm(false)}
          onSubmit={handleSessionSubmit}
        />
      )}
    </div>
  );
};

export default WellnessHostDashboard;